import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Star, MessageSquare, Check, EyeOff, Eye, Trash2, Loader2, Film, ShieldCheck, RefreshCw, Filter } from "lucide-react";

const TABS = [
  { key: "pending",  label: "Zur Freigabe", tone: "#EEDC82" },
  { key: "approved", label: "Freigegeben",  tone: "#8FBC8F" },
  { key: "hidden",   label: "Versteckt",    tone: "#EE9CA1" },
  { key: "all",      label: "Alle",         tone: "#F5F0E6" },
];

function Stars({ value, size = 12 }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size}
          fill={i < Math.round(value || 0) ? "#D4AF37" : "transparent"}
          color="#D4AF37" />
      ))}
    </div>
  );
}

function StatusChip({ r }) {
  let label = "offen"; let tone = "#EEDC82";
  if (r.hidden) { label = "versteckt"; tone = "#EE9CA1"; }
  else if (r.approved) { label = "freigegeben"; tone = "#8FBC8F"; }
  return (
    <span className="text-[9px] uppercase tracking-[0.3em] px-2 py-0.5 rounded-full border"
      style={{ borderColor: `${tone}66`, color: tone }}>
      {label}
    </span>
  );
}

export default function Reviews() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, hidden: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reviews?status=${tab}`);
      setRows(data.reviews || []);
      setCounts(data.counts || { pending: 0, approved: 0, hidden: 0 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Konnte Bewertungen nicht laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [tab]);

  const patch = async (id, body, msg) => {
    setBusy(id);
    try {
      await api.patch(`/admin/reviews/${id}`, body);
      toast.success(msg);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    } finally { setBusy(null); }
  };
  const del = async (id) => {
    if (!window.confirm("Diese Bewertung dauerhaft löschen? Kann nicht rückgängig gemacht werden.")) return;
    setBusy(id);
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Bewertung gelöscht");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    } finally { setBusy(null); }
  };

  const sortedRows = useMemo(() => rows, [rows]);

  return (
    <div className="space-y-6" data-testid="admin-reviews-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Community · Moderation</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl flex items-center gap-3">
            <MessageSquare size={30} className="text-[#D4AF37]" /> Bewertungen
          </h1>
          <div className="mt-2 text-xs text-[#A8A398] max-w-2xl">
            Neue Bewertungen von Gästen sind erst öffentlich sichtbar, sobald du sie freigibst.
            Bewertungen mit gültigem Ticket-QR-Code werden automatisch freigegeben („verifiziert").
          </div>
        </div>
        <button onClick={load} data-testid="reviews-reload"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] px-3 py-2 rounded border border-[#2A2A2A] hover:border-[#D4AF37]">
          <RefreshCw size={12} /> Neu laden
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="reviews-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          const n = counts[t.key];
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`reviews-tab-${t.key}`}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] px-4 py-2 rounded-full border transitions-safe"
              style={{
                borderColor: active ? t.tone : "#2A2A2A",
                color: active ? "#000" : t.tone,
                background: active ? t.tone : "transparent",
              }}>
              <Filter size={11} /> {t.label}
              {n != null && t.key !== "all" && (
                <span className="ml-1 font-mono text-[10px] opacity-70">{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#575148]"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : sortedRows.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#2A2A2A] rounded-lg text-[#575148]">
          Keine Bewertungen in dieser Kategorie.
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {sortedRows.map((r) => (
            <div key={r.id} className="card-lux p-4" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Stars value={r.rating} size={14} />
                    <span className="font-mono text-sm text-[#D4AF37]">{Number(r.rating).toFixed(1)}<span className="text-xs text-[#575148]"> / 5</span></span>
                    <span className="text-sm text-[#F5F0E6]">{r.guest_name || "Gast"}</span>
                    {r.verified && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.3em] px-1.5 py-0.5 rounded-full border border-[#8FBC8F]/60 text-[#8FBC8F]">
                        <ShieldCheck size={10} /> Ticket verifiziert
                      </span>
                    )}
                    <StatusChip r={r} />
                  </div>
                  <div className="mt-1.5 text-[11px] uppercase tracking-[0.25em] text-[#A8A398] flex items-center gap-2 flex-wrap">
                    <Film size={11} className="text-[#D4AF37]" />
                    <span>{r.movie_title || r.movie_id}</span>
                    <span>·</span>
                    <span className="font-mono">{r.created_at ? new Date(r.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                    {r.guest_email && <><span>·</span><span className="lowercase">{r.guest_email}</span></>}
                  </div>
                  {r.comment && (
                    <p className="mt-2 text-sm text-[#F5F0E6]/85 leading-relaxed whitespace-pre-wrap">{r.comment}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {!r.approved && (
                    <button onClick={() => patch(r.id, { approved: true }, "Freigegeben")} disabled={busy === r.id}
                      data-testid={`review-approve-${r.id}`}
                      title="Freigeben — öffentlich sichtbar machen"
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded border border-[#8FBC8F]/60 text-[#8FBC8F] hover:bg-[#8FBC8F] hover:text-black disabled:opacity-40">
                      {busy === r.id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Freigeben
                    </button>
                  )}
                  {r.approved && !r.hidden && (
                    <button onClick={() => patch(r.id, { hidden: true }, "Versteckt")} disabled={busy === r.id}
                      data-testid={`review-hide-${r.id}`}
                      title="Verstecken — nicht mehr öffentlich sichtbar"
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded border border-[#EE9CA1]/60 text-[#EE9CA1] hover:bg-[#EE9CA1] hover:text-black disabled:opacity-40">
                      {busy === r.id ? <Loader2 size={11} className="animate-spin" /> : <EyeOff size={11} />} Verstecken
                    </button>
                  )}
                  {r.hidden && (
                    <button onClick={() => patch(r.id, { approved: true, hidden: false }, "Wieder freigegeben")} disabled={busy === r.id}
                      data-testid={`review-restore-${r.id}`}
                      title="Wieder sichtbar machen"
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded border border-[#EEDC82]/60 text-[#EEDC82] hover:bg-[#EEDC82] hover:text-black disabled:opacity-40">
                      {busy === r.id ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />} Wiederherstellen
                    </button>
                  )}
                  <button onClick={() => del(r.id)} disabled={busy === r.id}
                    data-testid={`review-delete-${r.id}`}
                    title="Endgültig löschen"
                    className="inline-flex items-center px-2 py-1.5 rounded border border-[#2A2A2A] hover:border-[#7A1921] hover:text-[#7A1921] disabled:opacity-40">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
